import React from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '../SectionHeading';
import { cardHover, fadeUp, slideInLeft, slideInRight, stagger, viewport } from '../motion';

const philosophy = [
  "Building elegant, user-friendly solutions",
  "Continuous learning and adaptation",
  "Clean, maintainable, and efficient code",
  "Team collaboration and knowledge sharing",
  "Turning complex problems into simple solutions",
];

function About() {
  return (
    <section id="about" className="relative overflow-hidden py-20">
      {/* Decorative elements */}
      <div className="absolute right-0 top-0 h-32 w-32 animate-blob-medium rounded-full bg-indigo-200 opacity-20 mix-blend-multiply blur-xl"></div>
      <div className="absolute bottom-0 left-0 h-40 w-40 animate-blob-slow rounded-full bg-blue-200 opacity-20 mix-blend-multiply blur-xl"></div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Introduction" title="About Me" className="mb-12" />

        <div className="flex flex-col items-center gap-10 lg:flex-row lg:items-stretch">
          {/* Left: the narrative */}
          <motion.div
            className="lg:w-1/2"
            variants={stagger(0.14)}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.h3
              variants={slideInLeft}
              className="mb-5 text-center text-2xl font-bold text-gray-800 lg:text-left lg:text-3xl"
            >
              Hi, I'm Mayur Adlak. Nice to meet you.
              <motion.span
                className="ml-2 inline-block origin-[70%_70%]"
                animate={{ rotate: [0, 18, 0, 18, 0] }}
                transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 5, ease: 'easeInOut' }}
              >
                👋
              </motion.span>
            </motion.h3>

            <motion.p variants={fadeUp} className="mb-5 text-justify text-lg leading-relaxed text-gray-600 [hyphens:auto]">
              Passionate problem solver and software developer. I thrive in collaborative
              environments, leveraging my skills to design and develop cutting-edge software
              applications, and staying current with industry trends and best practices.
            </motion.p>

            <motion.p variants={fadeUp} className="text-justify text-lg leading-relaxed text-gray-600 [hyphens:auto]">
              My goal is to create robust, scalable software that drives productivity and exceeds
              client expectations. Let's transform ideas into reality together.
            </motion.p>
          </motion.div>

          {/* Right: the philosophy card */}
          <motion.div
            className="flex lg:w-1/2 lg:justify-center"
            variants={slideInRight}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
          >
            <motion.div
              className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-8 shadow-lg transition-shadow duration-300 hover:shadow-2xl"
              whileHover={cardHover}
            >
              <div className="mb-5 flex items-center gap-3">
                <h3 className="whitespace-nowrap text-[11px] tracking-wide text-slate-500">
                  Professional Philosophy
                </h3>
                <span className="h-px flex-1 bg-gradient-to-r from-slate-300 to-transparent"></span>
              </div>

              <motion.ul
                className="space-y-3"
                variants={stagger(0.1, 0.15)}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
              >
                {philosophy.map((item) => (
                  <motion.li
                    key={item}
                    className="flex items-start gap-3"
                    variants={fadeUp}
                    whileHover={{ x: 5 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <svg
                      className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>

              <motion.div
                className="mt-6 flex items-center gap-2.5 rounded-lg border border-blue-100 bg-blue-50 p-3"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
              >
                <span className="relative flex h-2 w-2 flex-shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75 motion-reduce:animate-none"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
                </span>
                <p className="text-sm font-medium text-blue-700">
                  Open to new opportunities and collaborations
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default About;
